/**
 * Ölçüm aracı ve oturum yürütücüsü.
 *
 * nrg-core v1.0: altı alan, altı görev. Üç paralel form (A, B, C) aynı
 * yapıyı farklı uyarıcılarla sunar — ön test / ara / son testte aynı formu
 * tekrar ettirmemek için. Formlar yalnızca tohum türetmesinde ayrışır;
 * görev yapısı, deneme sayısı ve zorluk basamakları birebir aynıdır.
 * Form eşdeğerliği iddiasının teknik karşılığı bu.
 *
 * Yürütücü DOM bilmez: sıradaki denemeyi verir, gözlemi alır, merdiveni
 * ilerletir. Uyarıcıyı çizmek ve zamanı ölçmek çağıranın işi.
 */

import { Rng, seedFrom } from './prng'
import { Staircase, DEFAULT_STAIRCASE, type StaircaseConfig } from './staircase'
import { TASKS, scoreAll } from './tasks'
import { judge, assessmentValidity } from './validity'
import type {
  InstrumentForm,
  ScoreRow,
  TaskCode,
  TrialObservation,
  TrialRecord,
  TrialSpec,
} from './types'

export const INSTRUMENT_CODE = 'nrg-core'
export const INSTRUMENT_VERSION = '1.0'

/** Görev sırası: dikkat görevleri yorgunluk etkisini azaltmak için bölünür */
export const TASK_ORDER: TaskCode[] = [
  'cpt',
  'span_visual',
  'flanker',
  'nback',
  'symbol_search',
  'switching',
]

export function form(code: 'A' | 'B' | 'C'): InstrumentForm {
  return {
    instrumentCode: INSTRUMENT_CODE,
    instrumentVersion: INSTRUMENT_VERSION,
    form: code,
    tasks: TASK_ORDER,
  }
}

/** Uyarlanabilir görevlerin merdiven ayarları */
const STAIRCASES: Partial<Record<TaskCode, Partial<StaircaseConfig>>> = {
  span_visual: { start: 3, min: 2, max: 9, down: 2, up: 1, maxTrials: 30, reversalsToStop: 8 },
  nback: { start: 1, min: 1, max: 4, down: 3, up: 1, maxTrials: 60, reversalsToStop: 6 },
}

export interface SessionOptions {
  form: 'A' | 'B' | 'C'
  /** Danışan/ölçüm başına benzersiz: aynı çocuk aynı uyarıcıyı iki kez görmesin */
  salt: string
  /** Yalnızca belirli görevleri koştur (kısa izleme seti için) */
  only?: TaskCode[]
}

export interface SessionState {
  taskIndex: number
  trialInTask: number
  done: boolean
}

export class AssessmentSession {
  readonly form: InstrumentForm
  private readonly tasks: TaskCode[]
  private readonly baseSeed: number
  private readonly staircases = new Map<TaskCode, Staircase>()
  private readonly history = new Map<TaskCode, TrialSpec[]>()
  private readonly records: TrialRecord[] = []
  private taskIndex = 0
  private trialInTask = 0
  private pending: TrialSpec | null = null
  private globalIndex = 0

  constructor(opts: SessionOptions) {
    this.form = form(opts.form)
    this.tasks = opts.only ?? TASK_ORDER
    this.baseSeed = seedFrom(INSTRUMENT_CODE, INSTRUMENT_VERSION, opts.form, opts.salt)
    for (const code of this.tasks) {
      const cfg = STAIRCASES[code]
      if (TASKS[code].adaptive) {
        this.staircases.set(code, new Staircase(cfg ?? DEFAULT_STAIRCASE))
      }
      this.history.set(code, [])
    }
  }

  get state(): SessionState {
    return { taskIndex: this.taskIndex, trialInTask: this.trialInTask, done: this.done }
  }

  get done(): boolean {
    return this.taskIndex >= this.tasks.length
  }

  get currentTask(): TaskCode | null {
    return this.tasks[this.taskIndex] ?? null
  }

  /**
   * Sıradaki denemeyi üretir. İki kez çağrılırsa aynı denemeyi verir —
   * çalıştırma katmanı yeniden çizmek isterse diye.
   */
  next(): TrialSpec | null {
    if (this.pending) return this.pending
    if (this.done) return null

    const code = this.tasks[this.taskIndex]!
    const task = TASKS[code]
    const stair = this.staircases.get(code)

    // Görev bitti mi: merdiven durduysa ya da deneme sayısı dolduysa
    const stairDone = stair?.finished ?? false
    if (this.trialInTask >= task.trials || stairDone) {
      this.taskIndex++
      this.trialInTask = 0
      return this.next()
    }

    const level = stair ? stair.level : this.fixedLevel(code)
    const rng = new Rng(this.baseSeed).stream(code)

    this.pending = task.generate({
      index: this.globalIndex,
      level,
      rngSeed: rng.uint32(),
      history: this.history.get(code)!,
    })
    return this.pending
  }

  /**
   * Sabit zorluklu görevlerde seviye blok ortasında bir kez yükselir:
   * görev boyunca tek bir zorlukta kalmak tavan/taban etkisi üretir.
   */
  private fixedLevel(code: TaskCode): number {
    const total = TASKS[code].trials
    return 1 + Math.floor((this.trialInTask / total) * 6)
  }

  /** Gözlemi işler, merdiveni ilerletir, kaydı döndürür */
  submit(obs: TrialObservation): TrialRecord {
    const spec = this.pending
    if (!spec) throw new Error('submit: önce next() çağrılmalı')

    const j = judge(spec, obs)
    const record: TrialRecord = {
      index: spec.index,
      task: spec.task,
      domain: spec.domain,
      level: spec.level,
      isTarget: spec.isTarget,
      stimulusAtMs: obs.stimulusAtMs,
      responseAtMs: obs.responseAtMs,
      rtMs: j.rtMs,
      outcome: j.outcome,
      correct: j.correct,
      response: obs.response,
      expected: spec.correct,
      framesDropped: obs.framesDropped,
      presentationLagMs: j.lagMs,
      valid: j.valid,
      invalidReason: j.invalidReason,
      stimulus: spec.stimulus,
    }

    this.records.push(record)
    this.history.get(spec.task)!.push(spec)

    const stair = this.staircases.get(spec.task)
    if (stair) stair.record(j.correct, j.valid)

    this.pending = null
    this.trialInTask++
    this.globalIndex++
    return record
  }

  get trials(): readonly TrialRecord[] {
    return this.records
  }

  /** Oturum sonucu: skorlar + güvenilirlik değerlendirmesi */
  result(): {
    instrumentCode: string
    instrumentVersion: string
    form: 'A' | 'B' | 'C'
    scores: ScoreRow[]
    invalidRatio: number
    trustworthy: boolean
    trialCount: number
  } {
    const v = assessmentValidity(this.records)
    return {
      instrumentCode: INSTRUMENT_CODE,
      instrumentVersion: INSTRUMENT_VERSION,
      form: this.form.form,
      scores: scoreAll(this.records),
      invalidRatio: v.invalidRatio,
      trustworthy: v.trustworthy,
      trialCount: this.records.length,
    }
  }
}

/**
 * Alıştırma denemeleri.
 *
 * Ölçüm doğrudan gerçek denemelerle başlarsa ilk denemeler dikkati değil
 * "görevi anlamayı" ölçer ve bu, ölçümün başını sistemli olarak bozar.
 * Alıştırma bloğu puanlanmaz, kaydedilmez; yalnızca çocuk ne yapacağını
 * öğrensin diye vardır.
 *
 * İki farkı var: her zaman en kolay seviyede ve süreler uzatılmış.
 * Tohumu da ayrı — alıştırmada gördüğü uyarıcı gerçek blokta tekrar
 * karşısına çıkmaz.
 */
export function practiceTrials(
  task: TaskCode,
  opts: { salt: string; count?: number; slowdown?: number } = { salt: 'practice' },
): TrialSpec[] {
  const count = opts.count ?? 6
  const slow = opts.slowdown ?? 1.5
  const base = seedFrom(INSTRUMENT_CODE, 'practice', opts.salt, task)
  const def = TASKS[task]
  const history: TrialSpec[] = []
  const out: TrialSpec[] = []

  for (let i = 0; i < count; i++) {
    const spec = def.generate({
      index: i,
      level: 1,
      rngSeed: new Rng(base).stream(task + ':' + i).uint32(),
      history,
    })
    const slowed: TrialSpec = {
      ...spec,
      durationMs: Math.round(spec.durationMs * slow),
      isiMs: Math.round(spec.isiMs * slow),
      responseWindowMs: Math.round(spec.responseWindowMs * slow),
    }
    history.push(slowed)
    out.push(slowed)
  }
  return out
}

/**
 * Kaydedilmiş denemelerden yeniden puanlama.
 *
 * Norm çalışması yayımlandığında ya da puanlama algoritması düzeltildiğinde
 * geçmiş tüm ölçümler bu fonksiyonla yeniden hesaplanır. Veritabanı eski
 * skor satırlarını korur, bunlar yeni sürüm olarak eklenir.
 */
export function rescore(trials: TrialRecord[]): ScoreRow[] {
  return scoreAll(trials)
}

/* ------------------------------------------------------------------ */

/**
 * Bir görevin yaklaşık süresi (saniye).
 *
 * Sabit bir tablo değil, görevin KENDİ tanımından hesaplanıyor: uyarıcı
 * süresi + denemeler arası boşluk + tepki penceresinin yarısı. Sabit
 * tablo tutsaydık bir görevin deneme sayısı değiştiğinde süre yalan
 * söylemeye başlardı ve kimse fark etmezdi.
 *
 * Uyarlanabilir görevlerde deneme sayısı merdivene göre erken bitebilir;
 * bu yüzden çıkan sayı ÜST SINIRA yakın. Uygulayıcıya "yaklaşık" diye
 * gösterilmesi gerekiyor.
 */
export function gorevSuresiSn(task: TaskCode, level = 4): number {
  const def = TASKS[task]
  const spec = def.generate({
    index: 0,
    level,
    rngSeed: seedFrom('sure', task, level),
    history: [],
  })
  /**
   * `responseWindowMs` denemenin TAMAMINI kapsıyor (uyarıcı + boşluk),
   * bu yüzden üstüne durationMs eklemek süreyi iki katına çıkarıyor.
   *
   * İki akış var: sabit hızlı görevlerde (uyarıcı akıp gidiyor, çocuk
   * yetişiyor) pencere her denemede tamamen doluyor. Seçim ve dizi
   * görevlerinde ise deneme çocuk cevaplayınca bitiyor; pencere üst
   * sınır. Orada beklenen süre pencerenin tamamı değil.
   */
  const sabitHiz = spec.responseWindowMs <= spec.durationMs + spec.isiMs + 1
  const denemeMs = (sabitHiz ? spec.responseWindowMs : spec.responseWindowMs * 0.6) + ITI_MS
  const alistirmaMs = 6 * (denemeMs * 1.5)
  return Math.round((def.trials * denemeMs + alistirmaMs + YONERGE_MS) / 1000)
}

/** Seçilen görev setinin toplam süresi (dakika) */
export function olcumSuresiDk(tasks: TaskCode[]): number {
  const sn = tasks.reduce((a, t) => a + gorevSuresiSn(t), 0)
  return Math.max(1, Math.round(sn / 60))
}

/** Denemeler arası boşluk — oynatıcıdaki değerle aynı olmalı */
const ITI_MS = 400
/** Görev başı yönerge ve hazırlık için kabaca ayrılan süre */
const YONERGE_MS = 25_000
